import { PropsWithChildren } from "react";
import { Control, Controller, FieldValues, Path, PathValue } from "react-hook-form";
import { Autocomplete, SxProps, TextField, Theme } from "@mui/material";

import { CommonFieldProps } from "./types";

interface Props<T extends FieldValues> extends CommonFieldProps<T> {
    control: Control;
    options: any[];
    labelId: string;
    label: string;
    onSelect?: (value: any) => void;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    containerSx?: SxProps<Theme>;
}

const getValueProps = (value: string | any, options: any[]): any[] | undefined => {
    if (Array.isArray(value)) { return value; }

    var element = options.find((el) => {
        if (Array.isArray(el)) { return el[0] === value; }
        return value === el;
    });
    if (element && !Array.isArray(element)) { return [element, element]; }

    return element;
}

const FieldAutocompleteList: React.FunctionComponent<Props<any>> = <TObject extends FieldValues>({
    control,
    fieldName,
    options,
    labelId,
    label,
    onSelect,
    children,
    disabled = false,
    sx = {},
    containerSx = {},
}: PropsWithChildren<Props<TObject>>) => (
    <Controller
        control={control}
        name={fieldName}
        defaultValue={null as PathValue<TObject, Path<TObject>>}
        render={({field: {onChange, ..._field}, fieldState: { error }}) => (
            <Autocomplete
                id={labelId}
                options={options}
                disabled={disabled}
                getOptionLabel={(option) => getValueProps(option, options) !== undefined ? getValueProps(option, options)![1] : ''}
                onChange={(_, data) => {
                    var option = getValueProps(data, options) || [];
                    onChange(option.length > 0 ? option[0] : null);
                    if (onSelect) onSelect(option.length > 0 ? option[0] : null);
                }}
                isOptionEqualToValue={(option, value) => {
                    if (getValueProps(option, options) === undefined || getValueProps(value, options) === undefined) return false;

                    return getValueProps(option, options)![0] === getValueProps(value, options)![0];
                }}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={getValueProps(option, options)![0]}>
                            {getValueProps(option, options)![1]}
                        </li>
                    );
                }}
                sx={containerSx}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={label}
                        variant="outlined"
                        error={error ? true : false}
                        helperText={error && error.message}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "disabled"
                        }}
                        sx={sx}
                    />
                )}
                {..._field}
            />
        )}
    />
);

export default FieldAutocompleteList;